import React, { useState, useEffect } from 'react';
import { useBandeau } from '../BandeauContext';
import { apiClient } from '../../utils/apiClient';

const API = process.env.REACT_APP_API_BASE_URL + '/schedule_api.php';

const PRIORITY_LABELS = ['', 'Faible', 'Basse', 'Normale', 'Haute', 'Prioritaire'];
const DIFFICULTY_LABELS = ['', 'Très facile', 'Facile', 'Moyen', 'Difficile', 'Très difficile'];

function computePeriodAvg(grades) {
    let sumNum = 0, sumDen = 0;
    grades.forEach(g => {
        const coef     = parseFloat(g.coefficient || 1);
        const gradeVal = parseFloat(g.grade);
        const maxVal   = parseFloat(g.max_grade || 20);
        if (parseInt(g.normalize_to_20) !== 0) {
            // ramené sur 20 : normalise d'abord, dénominateur = 20
            sumNum += (gradeVal / maxVal) * 20 * coef;
            sumDen += 20 * coef;
        } else {
            // pas ramené : garde le barème d'origine dans le dénominateur
            sumNum += gradeVal * coef;
            sumDen += maxVal * coef;
        }
    });
    if (sumDen === 0) return null;
    return Math.round(sumNum / sumDen * 20 * 100) / 100;
}

const SELECT_SM = "px-2 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-cyan-400";

function GoalsContent({ subjects, goals, gradeHistory: initHistory, periodGoals: initPeriodGoals, onSaved }) {
    const goalMap = {};
    goals.forEach(g => { goalMap[g.subject_id] = g; });

    const [form, setForm] = useState(() => {
        const init = {};
        subjects.forEach(s => {
            init[s.id] = {
                priority:   goalMap[s.id]?.priority   ?? 3,
                difficulty: goalMap[s.id]?.difficulty ?? 3,
            };
        });
        return init;
    });

    const [periodGoalMap, setPeriodGoalMap] = useState(() => {
        const map = {};
        (initPeriodGoals ?? []).forEach(pg => {
            map[`${pg.subject_id}_${pg.school_year}_${pg.period_type}_${pg.period_number}`] = parseFloat(pg.target_grade);
        });
        return map;
    });

    const [history, setHistory] = useState(initHistory ?? {});
    const [addingFor, setAddingFor] = useState(null);
    const today = new Date().toISOString().slice(0, 10);
    const [newGrade, setNewGrade] = useState({
        grade: '', max_grade: '20', coefficient: '1',
        label: '', graded_at: today, period_type: 'trimestre', period_number: 1, normalize_to_20: 1,
    });
    const [addingSaving, setAddingSaving] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');
    const [editingGrade, setEditingGrade] = useState(null); // { id, sid, grade, max_grade, ... }
    const [editSaving, setEditSaving] = useState(false);

    // Helpers période courante
    const [todayY, todayM] = today.split('-').map(Number);
    const currentSchoolYear = todayM >= 9 ? `${todayY}-${todayY + 1}` : `${todayY - 1}-${todayY}`;
    const currentPeriodNum = (type) => {
        if (type === 'semestre') return (todayM >= 9 || todayM === 1) ? 1 : 2;
        if (todayM >= 9 && todayM <= 11) return 1;
        if (todayM === 12 || todayM <= 2) return 2;
        return 3;
    };
    const isCurrentOrFuture = (p) => {
        if (p.school_year > currentSchoolYear) return true;
        if (p.school_year < currentSchoolYear) return false;
        return p.period_number >= currentPeriodNum(p.period_type);
    };

    const setField = (id, field, val) => setForm(p => ({ ...p, [id]: { ...p[id], [field]: val } }));

    const sorted = [...subjects].sort((a, b) => {
        const pa = form[a.id]?.priority ?? goalMap[a.id]?.priority ?? 3;
        const pb = form[b.id]?.priority ?? goalMap[b.id]?.priority ?? 3;
        return pb - pa || a.name.localeCompare(b.name);
    });

    const save = async () => {
        setSaving(true);
        const periodGoals = Object.entries(periodGoalMap)
            .filter(([, tg]) => tg !== null && !isNaN(tg))
            .map(([key, tg]) => {
                const [sid, schoolYear, periodType, periodNum] = key.split('_');
                return { subject_id: parseInt(sid), school_year: schoolYear, period_type: periodType, period_number: parseInt(periodNum), target_grade: tg };
            });
        const payload = subjects.map(s => ({
            subject_id: s.id,
            priority:   form[s.id]?.priority   ?? 3,
            difficulty: form[s.id]?.difficulty ?? 3,
        }));
        await apiClient(`${API}?action=save_goals`, {
            method: 'POST',
            body: JSON.stringify({ goals: payload, period_goals: periodGoals }),
        });
        setSaving(false);
        setSaveMsg('Objectifs enregistrés.');
        setTimeout(() => { setSaveMsg(''); onSaved(); }, 1200);
    };

    const submitGrade = async (sid) => {
        if (!newGrade.grade) return;
        setAddingSaving(true);
        const res = await apiClient(`${API}?action=add_grade`, {
            method: 'POST',
            body: JSON.stringify({
                subject_id:     sid,
                grade:          newGrade.grade,
                max_grade:      newGrade.max_grade,
                coefficient:    newGrade.coefficient,
                label:          newGrade.label,
                graded_at:      newGrade.graded_at,
                period_type:    newGrade.period_type,
                period_number:  newGrade.period_number,
                normalize_to_20: newGrade.normalize_to_20,
            }),
        }).then(r => r.json());
        if (res.success) {
            setHistory(prev => ({ ...prev, [sid]: res.history }));
            setAddingFor(null);
            setNewGrade({ grade: '', max_grade: '20', coefficient: '1', label: '', graded_at: today, period_type: 'trimestre', period_number: 1, normalize_to_20: 1 });
        }
        setAddingSaving(false);
    };

    const deleteGrade = async (sid, gid) => {
        await apiClient(`${API}?action=delete_grade`, { method: 'POST', body: JSON.stringify({ id: gid }) });
        setHistory(prev => ({ ...prev, [sid]: (prev[sid] || []).filter(g => g.id !== gid) }));
    };

    const updateGrade = async () => {
        if (!editingGrade?.grade) return;
        setEditSaving(true);
        const res = await apiClient(`${API}?action=update_grade`, {
            method: 'POST',
            body: JSON.stringify({
                id:              editingGrade.id,
                grade:           editingGrade.grade,
                max_grade:       editingGrade.max_grade,
                coefficient:     editingGrade.coefficient,
                label:           editingGrade.label,
                graded_at:       editingGrade.graded_at,
                period_type:     editingGrade.period_type,
                period_number:   editingGrade.period_number,
                normalize_to_20: editingGrade.normalize_to_20,
            }),
        }).then(r => r.json());
        if (res.success) {
            setHistory(prev => ({ ...prev, [editingGrade.sid]: res.history }));
            setEditingGrade(null);
        }
        setEditSaving(false);
    };

    const openEdit = (g, sid) => setEditingGrade({
        id: g.id, sid,
        grade:           String(parseFloat(g.grade)),
        max_grade:       String(parseInt(g.max_grade || 20)),
        coefficient:     String(parseFloat(g.coefficient || 1)),
        label:           g.label || '',
        graded_at:       g.graded_at,
        period_type:     g.period_type,
        period_number:   g.period_number,
        normalize_to_20: parseInt(g.normalize_to_20) !== 0 ? 1 : 0,
    });

    const groupedPeriods = (sid) => {
        const grades = history[sid] || [];
        const map = {};
        grades.forEach(g => {
            const k = `${g.school_year}_${g.period_type}_${g.period_number}`;
            if (!map[k]) map[k] = { school_year: g.school_year, period_type: g.period_type, period_number: g.period_number, grades: [] };
            map[k].grades.push(g);
        });
        return Object.values(map).sort((a, b) =>
            b.school_year.localeCompare(a.school_year) || b.period_number - a.period_number
        );
    };

    const periodLabel = (p) =>
        `${p.period_type === 'semestre' ? 'S' : 'T'}${p.period_number} ${p.school_year}`;

    if (subjects.length === 0) {
        return (
            <p className="text-center text-gray-400 py-12 text-sm">
                Aucune matière enregistrée. Associe des matières à ton profil pour commencer.
            </p>
        );
    }

    return (
        <div className="space-y-3">
            {sorted.map(s => {
                const f = form[s.id] ?? {};
                const periods = groupedPeriods(s.id);
                const isAdding = addingFor === s.id;

                // Current-or-future period for the header
                const headerPeriod = periods.find(p => isCurrentOrFuture(p)) ?? null;
                const headerPgKey = headerPeriod
                    ? `${s.id}_${headerPeriod.school_year}_${headerPeriod.period_type}_${headerPeriod.period_number}`
                    : `${s.id}_${currentSchoolYear}_trimestre_${currentPeriodNum('trimestre')}`;
                const headerAvg    = headerPeriod ? computePeriodAvg(headerPeriod.grades) : null;
                const headerTarget = periodGoalMap[headerPgKey] ?? null;
                const headerGap    = headerAvg !== null && headerTarget !== null ? +(headerTarget - headerAvg).toFixed(2) : null;
                const headerPLbl   = headerPeriod ? periodLabel(headerPeriod)
                    : `T${currentPeriodNum('trimestre')} ${currentSchoolYear}`;

                return (
                    <div key={s.id} className="rounded-xl border border-gray-100 dark:border-white/[0.06] bg-gray-50/50 dark:bg-white/[0.02]">

                        {/* ── Bandeau haut ── */}
                        <div className="px-3 pt-3 pb-2 flex flex-wrap items-center gap-x-3 gap-y-2">

                            {/* Gauche : nom + période courante */}
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 flex-1 min-w-0">
                                <span className="text-sm font-semibold text-gray-800 dark:text-white leading-tight">{s.name}</span>
                                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide shrink-0">{headerPLbl}</span>
                                {headerAvg !== null && (
                                    <span className="text-sm font-bold text-gray-800 dark:text-white shrink-0">
                                        {+headerAvg.toFixed(2)}<span className="text-xs text-gray-400 font-normal">/20</span>
                                    </span>
                                )}
                                {headerGap !== null && (
                                    <span className={`text-xs font-medium shrink-0 ${headerGap > 0 ? 'text-orange-500' : 'text-emerald-500'}`}>
                                        {headerGap > 0 ? `+${headerGap}` : headerGap} pts
                                    </span>
                                )}
                            </div>

                            {/* Droite : objectif + bouton */}
                            <div className="flex items-center gap-2 shrink-0">
                                <div className="flex items-center gap-1">
                                    <span className="text-[10px] text-gray-400">obj.</span>
                                    <input type="number" min="0" max="20" step="0.5"
                                        value={headerTarget ?? ''}
                                        placeholder="—"
                                        onChange={e => setPeriodGoalMap(prev => ({ ...prev, [headerPgKey]: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                                        className="w-12 px-1 py-0.5 rounded border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-xs text-center text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                                    <span className="text-[10px] text-gray-400">/20</span>
                                </div>
                                <button onClick={() => {
                                    setAddingFor(isAdding ? null : s.id);
                                    setNewGrade({ grade: '', max_grade: '20', coefficient: '1', label: '', graded_at: today, period_type: 'trimestre', period_number: 1, normalize_to_20: 1 });
                                }} className={`shrink-0 px-2 py-1 rounded-lg text-xs font-medium border transition-all ${isAdding ? 'border-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400' : 'border-gray-200 dark:border-white/10 text-gray-500 hover:border-gray-300'}`}>
                                    {isAdding ? 'Annuler' : '+ Note'}
                                </button>
                            </div>
                        </div>

                        {/* Priorité + Difficulté */}
                        <div className="grid grid-cols-2 gap-2 px-3 pb-2.5">
                            <div>
                                <p className="text-[10px] text-gray-400 mb-1">Priorité</p>
                                <select value={f.priority ?? 3} onChange={e => setField(s.id, 'priority', +e.target.value)} className={SELECT_SM}>
                                    {[1,2,3,4,5].map(v => <option key={v} value={v}>{PRIORITY_LABELS[v]}</option>)}
                                </select>
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-400 mb-1">Difficulté ressentie</p>
                                <select value={f.difficulty ?? 3} onChange={e => setField(s.id, 'difficulty', +e.target.value)} className={SELECT_SM}>
                                    {[1,2,3,4,5].map(v => <option key={v} value={v}>{DIFFICULTY_LABELS[v]}</option>)}
                                </select>
                            </div>
                        </div>

                        {periods.length > 0 && (
                            <div className="px-3 space-y-1.5 mb-2">
                                {periods.map(p => {
                                    const avg = computePeriodAvg(p.grades);
                                    const pgKey = `${s.id}_${p.school_year}_${p.period_type}_${p.period_number}`;
                                    return (
                                        <div key={pgKey} className="rounded-lg bg-white/80 dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.08] p-2">
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 shrink-0">{periodLabel(p)}</span>
                                                {avg !== null && (
                                                    <span className="text-sm font-bold text-gray-800 dark:text-white">
                                                        {+avg.toFixed(2)}<span className="text-xs text-gray-400 font-normal">/20</span>
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {p.grades.map(g => (
                                                    <React.Fragment key={g.id}>
                                                        {editingGrade?.id === g.id ? (
                                                            <div className="w-full mt-1 p-2.5 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-500/20 space-y-2">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <div className="flex items-center gap-1">
                                                                        <input type="number" min="0" step="0.5"
                                                                            value={editingGrade.grade}
                                                                            onChange={e => setEditingGrade(p => ({ ...p, grade: e.target.value }))}
                                                                            className="w-16 px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-center text-gray-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-amber-400" />
                                                                        <span className="text-xs text-gray-500">/</span>
                                                                        <input type="number" min="1" step="1"
                                                                            value={editingGrade.max_grade}
                                                                            onChange={e => setEditingGrade(p => ({ ...p, max_grade: e.target.value }))}
                                                                            className="w-14 px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-center text-gray-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-amber-400" />
                                                                    </div>
                                                                    <div className="flex gap-1">
                                                                        {['20', '10', '5'].map(m => (
                                                                            <button key={m} type="button"
                                                                                onClick={() => setEditingGrade(p => ({ ...p, max_grade: m }))}
                                                                                className={`px-1.5 py-0.5 rounded text-[10px] font-medium border transition-all ${editingGrade.max_grade === m ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-400' : 'border-gray-200 dark:border-white/10 text-gray-400 hover:border-gray-300'}`}>
                                                                                /{m}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                    <div className="flex items-center gap-1 ml-auto">
                                                                        <span className="text-xs text-gray-500">coef</span>
                                                                        <input type="number" min="0.5" max="10" step="0.5"
                                                                            value={editingGrade.coefficient}
                                                                            onChange={e => setEditingGrade(p => ({ ...p, coefficient: e.target.value }))}
                                                                            className="w-14 px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-center text-gray-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-amber-400" />
                                                                    </div>
                                                                </div>
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <input type="text" placeholder="Type (DS, exam…)" maxLength={50}
                                                                        value={editingGrade.label}
                                                                        onChange={e => setEditingGrade(p => ({ ...p, label: e.target.value }))}
                                                                        className="flex-1 min-w-[100px] px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-amber-400" />
                                                                    <input type="date"
                                                                        value={editingGrade.graded_at}
                                                                        onChange={e => setEditingGrade(p => ({ ...p, graded_at: e.target.value }))}
                                                                        className="px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-amber-400" />
                                                                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                                                        <input type="checkbox" checked={editingGrade.normalize_to_20 === 1}
                                                                            onChange={e => setEditingGrade(p => ({ ...p, normalize_to_20: e.target.checked ? 1 : 0 }))}
                                                                            className="w-3 h-3 accent-amber-500" />
                                                                        <span className="text-xs text-gray-500">/20</span>
                                                                    </label>
                                                                    <div className="flex gap-2 ml-auto">
                                                                        <button onClick={() => setEditingGrade(null)} className="px-2 py-1 rounded-lg text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5">Annuler</button>
                                                                        <button onClick={updateGrade} disabled={!editingGrade.grade || editSaving}
                                                                            className="px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium disabled:opacity-50 transition-colors">
                                                                            {editSaving ? '…' : 'Modifier'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="group relative inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-xs text-gray-600 dark:text-gray-300 cursor-pointer hover:border-amber-300 dark:hover:border-amber-500/50 transition-colors"
                                                                onClick={() => openEdit(g, s.id)}>
                                                                <span className="font-medium">{+parseFloat(g.grade).toFixed(2)}/{parseInt(g.max_grade || 20)}</span>
                                                                {parseFloat(g.coefficient || 1) !== 1 && (
                                                                    <span className="text-gray-400">×{g.coefficient}</span>
                                                                )}
                                                                <span className="text-gray-400">{g.graded_at?.slice(5).replace('-', '/')}</span>
                                                                {g.label && <span className="text-gray-400 hidden sm:inline">· {g.label}</span>}
                                                                <button onClick={(e) => { e.stopPropagation(); deleteGrade(s.id, g.id); }}
                                                                    className="hidden group-hover:inline ml-0.5 text-red-400 hover:text-red-600 leading-none">×</button>
                                                            </span>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {periods.length === 0 && !isAdding && (
                            <p className="px-3 text-xs text-gray-400 italic mb-2">Aucune note enregistrée</p>
                        )}

                        {isAdding && (
                            <div className="mx-3 mb-3 p-2.5 rounded-lg bg-cyan-50/50 dark:bg-cyan-900/10 border border-cyan-200/50 dark:border-cyan-500/20 space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="flex items-center gap-1">
                                        <input type="number" min="0" step="0.5" placeholder="Note"
                                            value={newGrade.grade}
                                            onChange={e => setNewGrade(p => ({ ...p, grade: e.target.value }))}
                                            className="w-16 px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-center text-gray-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                                        <span className="text-xs text-gray-500">/</span>
                                        <input type="number" min="1" step="1"
                                            value={newGrade.max_grade}
                                            onChange={e => setNewGrade(p => ({ ...p, max_grade: e.target.value }))}
                                            className="w-14 px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-center text-gray-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                                    </div>
                                    <div className="flex gap-1">
                                        {['20', '10', '5'].map(m => (
                                            <button key={m} type="button"
                                                onClick={() => setNewGrade(p => ({ ...p, max_grade: m }))}
                                                className={`px-1.5 py-0.5 rounded text-[10px] font-medium border transition-all ${newGrade.max_grade === m ? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 border-cyan-400' : 'border-gray-200 dark:border-white/10 text-gray-400 hover:border-gray-300'}`}>
                                                /{m}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-1 ml-auto">
                                        <span className="text-xs text-gray-500">coef</span>
                                        <input type="number" min="0.5" max="10" step="0.5"
                                            value={newGrade.coefficient}
                                            onChange={e => setNewGrade(p => ({ ...p, coefficient: e.target.value }))}
                                            className="w-14 px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-center text-gray-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="flex gap-1">
                                        {['trimestre', 'semestre'].map(pt => (
                                            <button key={pt} type="button"
                                                onClick={() => setNewGrade(p => ({ ...p, period_type: pt, period_number: Math.min(p.period_number, pt === 'semestre' ? 2 : 3) }))}
                                                className={`px-2 py-0.5 rounded text-[10px] font-medium border transition-all ${newGrade.period_type === pt ? 'bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 border-indigo-400' : 'border-gray-200 dark:border-white/10 text-gray-400 hover:border-gray-300'}`}>
                                                {pt === 'trimestre' ? 'Trimestre' : 'Semestre'}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex gap-1">
                                        {Array.from({ length: newGrade.period_type === 'semestre' ? 2 : 3 }, (_, i) => i + 1).map(n => (
                                            <button key={n} type="button"
                                                onClick={() => setNewGrade(p => ({ ...p, period_number: n }))}
                                                className={`w-8 py-0.5 rounded text-[10px] font-bold border transition-all ${newGrade.period_number === n ? 'bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 border-indigo-400' : 'border-gray-200 dark:border-white/10 text-gray-400 hover:border-gray-300'}`}>
                                                {newGrade.period_type === 'semestre' ? `S${n}` : `T${n}`}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <input type="text" placeholder="Type (DS, exam…)" maxLength={50}
                                        value={newGrade.label}
                                        onChange={e => setNewGrade(p => ({ ...p, label: e.target.value }))}
                                        className="flex-1 min-w-[100px] px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                                    <input type="date"
                                        value={newGrade.graded_at}
                                        onChange={e => setNewGrade(p => ({ ...p, graded_at: e.target.value }))}
                                        className="px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                        <input type="checkbox" checked={newGrade.normalize_to_20 === 1}
                                            onChange={e => setNewGrade(p => ({ ...p, normalize_to_20: e.target.checked ? 1 : 0 }))}
                                            className="w-3 h-3 accent-cyan-500" />
                                        <span className="text-xs text-gray-500">/20</span>
                                    </label>
                                    <button onClick={() => submitGrade(s.id)} disabled={!newGrade.grade || addingSaving}
                                        className="px-3 py-1 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-medium disabled:opacity-50 transition-colors">
                                        {addingSaving ? '…' : 'Ajouter'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {saveMsg && (
                <div className="px-4 py-3 rounded-2xl text-sm text-center font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
                    {saveMsg}
                </div>
            )}

            <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 dark:border-white/[0.06]">
                <button onClick={save} disabled={saving}
                    className="px-5 py-2.5 rounded-xl text-sm bg-cyan-500 hover:bg-cyan-600 text-white font-semibold disabled:opacity-50 transition-colors">
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
            </div>
        </div>
    );
}

export default function GradesView({ user, onExit }) {
    const [subjects, setSubjects] = useState([]);
    const [goals, setGoals] = useState([]);
    const [gradeHistory, setGradeHistory] = useState({});
    const [periodGoals, setPeriodGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { setBandeau, resetBandeau } = useBandeau();

    useEffect(() => {
        setBandeau({ breadcrumb: null, title: 'Notes' });
        return () => resetBandeau();
    }, [setBandeau, resetBandeau]);

    useEffect(() => {
        apiClient(`${API}?action=data`)
            .then(r => r.json())
            .then(data => {
                if (data.success) {
                    setSubjects(data.subjects || []);
                    setGoals(data.goals || []);
                    setGradeHistory(data.grade_history || {});
                    setPeriodGoals(data.period_goals || []);
                } else {
                    setError('Impossible de charger les données.');
                }
                setLoading(false);
            })
            .catch(() => { setError('Erreur de connexion.'); setLoading(false); });
    }, []);

    return (
        <div className="animate-fade-in relative z-10 text-gray-800 dark:text-gray-200 min-h-[80vh]">

            <div className="bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-6">
                {loading ? (
                    <div className="text-center py-12 text-gray-400 text-sm">Chargement…</div>
                ) : error ? (
                    <div className="text-center py-12 text-red-500 text-sm">{error}</div>
                ) : (
                    <GoalsContent
                        subjects={subjects}
                        goals={goals}
                        gradeHistory={gradeHistory}
                        periodGoals={periodGoals}
                        onSaved={onExit}
                    />
                )}
            </div>
        </div>
    );
}
